
import React, { useRef, useImperativeHandle, forwardRef } from "react";
import { UseFormReturn } from "react-hook-form";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { CampaignFormData } from "./CampaignFormSchema";

interface CampaignValueFieldProps {
  form: UseFormReturn<CampaignFormData>;
  campaignType: string;
  product: string;
}

export interface CampaignValueFieldRef {
  syncValueFromDOM: () => void;
}

export const CampaignValueField = forwardRef<CampaignValueFieldRef, CampaignValueFieldProps>(
  ({ form, campaignType, product }, ref) => {
    const inputRef = useRef<HTMLInputElement>(null);
    
    // Para campanhas welcome_bonus, não mostrar o campo de valor
    if (campaignType === 'welcome_bonus') {
      return null;
    }

    // Para campanhas birthday_campaign, não mostrar o campo de valor
    if (campaignType === 'birthday_campaign') {
      return null;
    }

    const getValueLabel = () => {
      if (campaignType === 'first_purchase') {
        return 'Porcentagem de Desconto (%)';
      }
      
      if (product === 'cashback') {
        switch (campaignType) {
          case 'accumulated_spending':
            return 'Valor Mínimo Gasto (R$)';
          case 'cashback_double':
            return 'Multiplicador do Cashback';
          case 'extended_period':
            return 'Dias extras de validade';
          default:
            return 'Valor';
        }
      } else if (product === 'referral') {
        switch (campaignType) {
          case 'bonus_referral':
            return 'Valor do bônus extra (R$)';
          case 'more_discount':
            return 'Percentual de desconto (%)';
          case 'double_bonus':
            return 'Multiplicador';
          default:
            return 'Valor';
        }
      }
      return 'Valor';
    };

    const getPlaceholder = () => {
      if (campaignType === 'first_purchase') {
        return 'Ex: 10 (para 10% de desconto)';
      }
      
      if (product === 'cashback') {
        switch (campaignType) {
          case 'accumulated_spending':
            return 'Ex: 200.00';
          case 'cashback_double':
            return 'Ex: 2 (para dobrar o cashback)';
          case 'extended_period':
            return 'Ex: 15 (dias extras)';
          default:
            return 'Digite o valor';
        }
      } else if (product === 'referral') {
        switch (campaignType) {
          case 'bonus_referral':
            return 'Ex: 50.00';
          case 'more_discount':
            return 'Ex: 15';
          case 'double_bonus':
            return 'Ex: 2';
          default:
            return 'Digite o valor';
        }
      }
      return 'Digite o valor';
    };

    const getHelperText = () => {
      if (campaignType === 'first_purchase') {
        return 'Porcentagem de desconto que será aplicada na primeira compra do cliente (1-100%)';
      }
      if (campaignType === 'accumulated_spending') {
        return 'Valor mínimo que o cliente deve gastar para ganhar o cashback';
      }
      return null;
    };

    const isRequired = () => {
      return ['first_purchase', 'accumulated_spending', 'bonus_referral', 'more_discount', 'double_bonus'].includes(campaignType);
    };

    // Função para processar o valor de forma consistente
    const processValue = (inputValue: string) => {
      if (inputValue === '' || inputValue === null || inputValue === undefined) {
        return undefined;
      }
      
      const numericValue = Number(inputValue);
      
      if (isNaN(numericValue)) {
        return undefined;
      }
      
      return numericValue;
    };

    // Função para sincronizar o valor do DOM com o formulário
    const syncValueFromDOM = () => {
      if (inputRef.current) {
        const domValue = inputRef.current.value;
        if (domValue && domValue !== '') {
          const processedValue = processValue(domValue);
          form.setValue('value', processedValue);
          return processedValue;
        }
      }
      return undefined;
    };

    // Expor a função de sincronização via ref
    useImperativeHandle(ref, () => ({
      syncValueFromDOM
    }));

    const helperText = getHelperText();

    return (
      <FormField
        control={form.control}
        name="value"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              {getValueLabel()} {isRequired() && <span className="text-red-500">*</span>}
            </FormLabel>
            <FormControl>
              <Input
                ref={inputRef}
                type="number"
                placeholder={getPlaceholder()}
                step={campaignType === 'first_purchase' || campaignType === 'more_discount' ? "1" : "0.01"}
                min="0"
                max={campaignType === 'first_purchase' || campaignType === 'more_discount' ? "100" : undefined}
                {...field}
                onInput={(e) => {
                  const processedValue = processValue(e.currentTarget.value);
                  field.onChange(processedValue);
                }}
                onChange={(e) => {
                  const processedValue = processValue(e.target.value);
                  field.onChange(processedValue);
                }}
                onBlur={(e) => {
                  // Força a sincronização do valor do DOM
                  syncValueFromDOM();
                  // Também chama o onBlur original se existir
                  if (field.onBlur) {
                    field.onBlur();
                  }
                }}
                value={field.value !== undefined && field.value !== null ? field.value.toString() : ''}
              />
            </FormControl>
            {helperText && (
              <p className="text-sm text-gray-600 mt-1">{helperText}</p>
            )}
            <FormMessage />
          </FormItem>
        )}
      />
    );
  }
);

CampaignValueField.displayName = "CampaignValueField";
